import _ from 'lodash';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { CAPTURE_MODE, ECG_VIEWER_CONFIG_STORE } from '@/shared/ui';
import { TYPE_EVENT_OTHER } from '@/shared/config';
import { useMergeState } from '@/shared/lib/customHooks';
import { toastrError } from '@/shared/lib/toastNotification';
import { checkAndProcessBeatData, countBeatChannels } from '@/entities/beat';
import { checkAndProcessEcgData, formatDrawDisplay, formatDrawMutation } from '@/entities/ecg';
import { logError } from '@/shared/lib/apiHelpers';
import type { EditStripModalProps } from './editStripModal';

/**
 * Backs the modal that lets a reviewer re-capture the ECG strip attached to an event. It seeds local state from the incoming strip, downloads a five minute ECG and beat window around the strip start or the device event's realtime range, builds the viewer descriptor from it, and exposes the save, cancel, capture, submit and retry handlers plus the resolved capture mode.
 *
 * @param props - Edit strip modal props with the strip data, ECG data map, viewer config, timezone offset, visibility and the save and close callbacks.
 * @returns An object with the local state, viewer ECG object, viewer and start epoch refs and the save, cancel, capture, capture mode, submit and retry handlers.
 */
const useEditStripModal = ({
  visible,
  isDeviceEvent = false,
  data,
  originalData,
  updateEditData,
  ecgDataMap,
  timezoneOffset,
  ecgViewerConfig,
  handleClickCloseButton,
}: EditStripModalProps) => {
  const [state, setState] = useMergeState<{
    isLoading: boolean;
    stripImgs: any[];
    draw: any[];
    stripStart: any;
    aiChannel: any;
    isUpdated: boolean;
    hrInfo?: any;
    start?: any;
    stop?: any;
    realtimeData?: any;
    viewerObjectVersion?: any;
  }>({
    isLoading: false,
    stripImgs: [],
    draw: [],
    stripStart: undefined,
    aiChannel: undefined,
    isUpdated: false,
  });

  const [ecgObject, setEcgObject] = useState<any>({});

  const aiECGViewerRef = useRef<any>(null);
  const startStripEpoch = useRef<any>(null);
  const stopStripEpoch = useRef<any>(null);

  let isActiveQuery: boolean;

  /**
   * Hands the edited strip back to the parent strips table. It passes the captured images, draw widgets, hrInfo and new start and stop only when something was actually updated, otherwise an empty patch, then resets the viewer's interaction mode.
   *
   * @returns Nothing.
   */
  const onClickSave = () => {
    const updateData = state.isUpdated
      ? {
          stripImgs: state.stripImgs,
          draw: state.draw,
          hrInfo: state.hrInfo,
          stripStart: state.start,
          stripStop: state.stop,
        }
      : {};
    updateEditData(updateData);
    if (aiECGViewerRef.current) {
      aiECGViewerRef.current.resetInMode();
    }
  };

  /**
   * Discards the pending strip edit and closes the modal. It clears the updated flag, images and draw from local state, calls the parent close handler and resets the viewer's interaction mode.
   *
   * @returns Nothing.
   */
  const onClickCancel = () => {
    setState({
      isUpdated: false,
      stripImgs: [],
      draw: [],
    });
    handleClickCloseButton();
    if (aiECGViewerRef.current) {
      aiECGViewerRef.current.resetInMode();
    }
  };

  /**
   * Builds the viewer descriptor that renders an editable strip inside the edit modal. It converts the strip start into a sample offset within the downloaded window, formats the stored draw widgets for display, pushes the event's gain and filter settings into the shared viewer config store, and sets the ECG object with the samples, AI beat prediction, fixed 25 mm per second speed and a ten second window.
   *
   * @param ecg - Decoded ECG channel samples for the window.
   * @param beats - AI beat sample positions.
   * @param hesBeatStatus - Per-beat classification statuses.
   * @param stripStart - Timestamp the capture mark should sit at.
   * @param exportEcgData - Raw ECG payload passed through for export.
   * @returns Nothing.
   */
  const createObjectEcg = (
    ecg: any,
    beats: any,
    hesBeatStatus: any,
    stripStart: any,
    exportEcgData: any,
  ) => {
    const { gain: configGain, hpFilter, lpFilter } = ecgViewerConfig || {};
    const { channels, gain, samplingFrequency } = ecgDataMap;
    // *: Add capture sample mark
    let drawStartSample = 0;
    const stripStartEpoch = moment(stripStart).valueOf();
    if (stripStartEpoch >= startStripEpoch.current && stripStartEpoch <= stopStripEpoch.current) {
      drawStartSample = Math.round(
        ((stripStartEpoch - startStripEpoch.current) / 1000) * samplingFrequency,
      );
    }
    const { draw, startSample } = formatDrawDisplay(
      state.draw,
      startStripEpoch.current,
      samplingFrequency,
      stopStripEpoch.current,
    );

    if (startSample) {
      drawStartSample = startSample;
    }

    const viewerObject = {
      options: {},
      widgets: draw,
    };
    if (!_.isNil(configGain)) {
      ECG_VIEWER_CONFIG_STORE.ampValue = configGain;
    }
    if (!_.isNil(hpFilter)) {
      ECG_VIEWER_CONFIG_STORE.hiPValue = hpFilter;
    }
    if (!_.isNil(lpFilter)) {
      ECG_VIEWER_CONFIG_STORE.loPValue = lpFilter;
    }
    setEcgObject({
      dataSamples: ecg,
      adcGain: gain,
      adcZero: 0,
      numChannels: channels.length,
      viewerObject,
      diagnosticLead: 2,
      samplingRate: samplingFrequency,
      events: undefined,
      aiPredict: [
        {
          beats,
          hesBeatStatus,
          channel: 1,
          hesEventStatus: _.times(hesBeatStatus.length, () => 1),
        },
      ],
      isAppliedAIByDefault: true,
      isShowVES: true,
      isShowSVES: true,
      isShowBeatMarkers: true,
      jumpOffset: drawStartSample,
      hiddenChannels: [0, 1, 2],
      hpFilters: hpFilter ?? ECG_VIEWER_CONFIG_STORE.hiPValue,
      notchFilters: lpFilter ?? ECG_VIEWER_CONFIG_STORE.loPValue,
      speed: 25,
      amp: configGain ?? ECG_VIEWER_CONFIG_STORE.ampValue,
      wndDuration: 10,
      exportEcgData,
    });
  };

  /**
   * Downloads the ECG samples and beats around the strip being edited. It floors the strip start onto a whole sample, takes the device event's realtime range when present or 150 seconds either side otherwise, fetches ECG padded by ten seconds together with the beats, trims that padding, counts the AI beat channels and builds the viewer object; cancelled requests are ignored and other failures logged.
   *
   * @param isRetry - When true the result is applied even if the effect that started the download is no longer active.
   * @returns A promise resolving once the viewer object is built or the failure has been handled.
   */
  const downloadDataEcg = async (isRetry?: boolean) => {
    setState({ isLoading: true });
    try {
      let convertStripStart = moment(state.stripStart);
      const stripStartMillisecond = convertStripStart.millisecond();
      const dividedNumber = 1000 / ecgDataMap.samplingFrequency;
      const floorNumber = Math.floor(stripStartMillisecond / dividedNumber) * dividedNumber;
      convertStripStart = convertStripStart.set('ms', floorNumber);
      const startStrip =
        isDeviceEvent && state.realtimeData?.start
          ? moment(state.realtimeData?.start)
          : convertStripStart.clone().add(-150, 's');
      const stopStrip =
        isDeviceEvent && state.realtimeData?.stop
          ? moment(state.realtimeData?.stop)
          : convertStripStart.clone().add(150, 's');
      const momentObject = {
        startMoment: startStrip,
        stopMoment: stopStrip,
      };
      const dataLength =
        ((stopStrip.valueOf() - startStrip.valueOf()) / 1000) * ecgDataMap.samplingFrequency;
      const exportMoment = {
        startMoment: moment(momentObject.startMoment).subtract(10, 'seconds'),
        stopMoment: moment(momentObject.stopMoment).add(10, 'seconds'),
      };
      const [ecgData, holterBeats] = (await Promise.all([
        checkAndProcessEcgData({
          ecgDataMap,
          dataLength,
          momentObject: exportMoment,
        } as any),
        checkAndProcessBeatData({
          ecgDataMap,
          momentObject,
        } as any),
      ])) as any;
      const addDataLength = 10 * ecgDataMap.samplingFrequency;
      const drawEcg = _.map(ecgData, (data: any) => _.slice(data, addDataLength, -addDataLength));
      if (isActiveQuery || isRetry) {
        startStripEpoch.current = startStrip.valueOf();
        stopStripEpoch.current = stopStrip.valueOf();
        const channel = countBeatChannels(holterBeats?.beatChannels);
        createObjectEcg(
          drawEcg,
          holterBeats?.beats || [],
          holterBeats?.hesBeatStatus || [],
          state.stripStart,
          { ecgData },
        );
        setState({
          aiChannel: channel,
          isLoading: false,
        });
      }
    } catch (error) {
      if (!_.includes(error.message, 'canceled')) {
        logError('Failed to download ECG data: ', error);
      }
      setState({ isLoading: false });
    }
  };

  /**
   * Asks the embedded ECG viewer to render and hand back the captured strip images. It defers the submit call by 50 milliseconds so the viewer has applied the freshly set draw widgets first.
   *
   * @returns Nothing.
   */
  const callCreateImage = () => {
    setTimeout(() => {
      if (aiECGViewerRef.current) {
        aiECGViewerRef.current.handleSubmit();
      }
    }, 50);
  };

  /**
   * Accepts the SVG strips captured from the viewer and records them as a pending edit. It converts the drawn widgets back into timestamps for the visible channels, stores the images, draw and new start and stop on local state for both capture modes, then triggers the viewer submit; failures are logged and surfaced as an error toast.
   *
   * @param draw - Widgets drawn on the viewer at capture time.
   * @param allIndexStripSvg - Captured SVG markup, one entry per strip index.
   * @param captureMode - Whether the capture is for an event or an other-type strip.
   * @param visibleChannels - Channels currently shown, used when converting draw positions.
   * @returns Nothing.
   */
  const handleCaptureImages = ({ draw, allIndexStripSvg, captureMode, visibleChannels }: any) => {
    try {
      if (allIndexStripSvg.length > 0) {
        const { samplingFrequency } = ecgDataMap;
        const { formatDraw, stripStart, stripStop } = formatDrawMutation(
          draw,
          samplingFrequency,
          startStripEpoch.current,
          timezoneOffset,
          visibleChannels,
        );
        if (captureMode === CAPTURE_MODE.EVENT) {
          setState({
            stripImgs: allIndexStripSvg,
            draw: formatDraw,
            isUpdated: true,
            start: stripStart,
            stop: stripStop,
          });
        } else if (captureMode === CAPTURE_MODE.OTHER) {
          setState({
            stripImgs: allIndexStripSvg,
            draw: formatDraw,
            isUpdated: true,
            start: stripStart,
            stop: stripStop,
          });
        }
        callCreateImage();
      }
    } catch (error) {
      logError('Failed to capture images: ', error);
      toastrError(error.message, 'Error');
    }
  };

  /**
   * Picks the capture mode the strip editor should run in for the event being edited. Anything other than an other-type event captures in event mode.
   *
   * @returns The capture mode constant for this event.
   */
  const getCaptureMode = () => {
    if (originalData?.type !== TYPE_EVENT_OTHER) {
      return CAPTURE_MODE.EVENT;
    }
    return CAPTURE_MODE.OTHER;
  };

  /**
   * Captures the viewer's full rendering state after a strip capture so it can be persisted with the event. It converts the viewer widgets into strip start and stop timestamps, writes the formatted widgets back onto the viewer object, and stores an hrInfo record with the beat markers, speed, amplitude, filters, hidden channels and beat display flags.
   *
   * @param ecgData - Viewer payload whose ecg field carries the current render settings.
   * @returns Nothing.
   */
  const onSubmit = (ecgData: any) => {
    const {
      beatMarkers,
      viewerObject,
      speed,
      amp,
      hpFilters,
      notchFilters,
      hiddenChannels,
      isShowBeatMarkers,
      isAppliedAIByDefault,
      isAIBeatMarkers,
      diagnosticLead,
      hesBeatStatus,
      isShowVES,
      isShowSVES,
      wndDuration,
      samplingRate,
    } = ecgData.ecg;
    const stripTimeObjects = [];
    const { samplingFrequency } = ecgDataMap;

    const { stripStart, stripStop, formatDraw } = formatDrawMutation(
      viewerObject.widgets,
      samplingFrequency,
      startStripEpoch.current,
      timezoneOffset,
      hiddenChannels,
    );
    stripTimeObjects.push({
      stripStart,
      stripStop,
    });
    viewerObject.widgets = formatDraw;
    const hrInfo = {
      viewerObject,
      beatMarkers,
      speed,
      amp,
      wndDuration,
      hpFilters,
      notchFilters,
      hiddenChannels,
      isShowBeatMarkers,
      isAppliedAIByDefault,
      isAIBeatMarkers,
      diagnosticLead,
      hesBeatStatus,
      isShowVES,
      isShowSVES,
      stripTimeObjects,
    };
    setState({ hrInfo });
  };

  /**
   * Retries the ECG download after a failed load in the edit modal. It re-runs the download with the retry flag so the result is applied regardless of the active query guard.
   *
   * @returns Nothing.
   */
  const onClickRetry = () => {
    downloadDataEcg(true);
  };

  useEffect(() => {
    if (data) {
      const { stripStart } = data;
      const { stripImgs, draw, realtimeData, viewerObjectVersion } = data;
      const setStateObject = {
        stripImgs,
        draw,
        stripStart,
        realtimeData,
        viewerObjectVersion,
        isUpdated: false,
      };
      setState(setStateObject);
    }
  }, [data]);

  useEffect(() => {
    isActiveQuery = true;
    if (state.stripStart && visible) {
      downloadDataEcg();
    } else {
      setEcgObject({});
    }
    return () => {
      isActiveQuery = false;
    };
  }, [state.stripStart, isDeviceEvent, visible]);

  return {
    state,
    ecgObject,
    aiECGViewerRef,
    startStripEpoch,
    onClickSave,
    onClickCancel,
    handleCaptureImages,
    getCaptureMode,
    onSubmit,
    onClickRetry,
  };
};

export default useEditStripModal;
